import { _, locale } from 'svelte-i18n';
import * as log from '$lib/log.js';
import { geo } from '$lib/locations.js';

/** @type {(id: string, options?: { default?: string }) => string} */
let translate;
const tunsub = _.subscribe(value => {
  translate = value;
});

export function formatPrice(number, round) {
  if (number > 1000000 && round) {
    return `${(number / 1000000).toPrecision(2)}mil`
  }

  return number.toLocaleString('cs-CZ', {
    maximumFractionDigits: 0,
  });
}

export function formatDatetime(timestamp) {
  var date = new Date(timestamp * 1000);
  const options = {
    dateStyle: "medium",
    timeStyle: "long",
    // timeZone: "Czechia/Prague",
  };
  const formatter = new Intl.DateTimeFormat('cs-CZ', options);

  return formatter.format(date);
}

/**
 * Localized label for an advert floorplan. 'unique' means no standard
 * disposition was recognized by the scrapper
 * @param {string} floorplan
 * @returns {string}
 */
export function formatFloorplan(floorplan) {
  if (floorplan === 'unique') {
    return translate('preview.unique_floorplan', { default: 'Unique layout' })
  }

  return floorplan;
}

export function formatFloorplans(floorplans, maxLength) {
  if (floorplans.length == maxLength || floorplans == 0) {
    return translate('lib.all_floor_plans', { default: 'all floor plans' })
  }

  return floorplans.map(formatFloorplan).join(', ');
}

export function get_user_language_code() {
  var lang = localStorage.getItem("lang");
  if (lang !== null) {
    return lang;
  }

  if (window.Telegram.WebApp.initDataUnsafe == "") {
    return;
  }
  if (window.Telegram.WebApp.initDataUnsafe.user == null) {
    return;
  }
  if (window.Telegram.WebApp.initDataUnsafe.user.language_code == "") {
    return;
  }

  lang = window.Telegram.WebApp.initDataUnsafe.user.language_code;
  localStorage.setItem("lang", lang);

  return lang;
}

export async function login(initData) {
  if (sessionStorage.getItem("token") !== null) {
    console.log("Token already exists")
    return Promise.resolve();
  }

  const request = new Request('/api/login?' + initData)

  return fetch(request).then((response) => {
    if (!response.ok) {
      let err = new Error(`/login request failed. status: ${response.status}`);
      return Promise.reject(err);
    }

    return response.json();
  }).then((data) => {
    console.log('Login succeed. Saving token...')
    sessionStorage.setItem("token", data['token']);
  }).catch((error) => {
    let err = new Error('/login failed to parse data', { cause: error });
    return Promise.reject(err);
  });
}

export async function fetchAdvert(body) {
  let token = sessionStorage.getItem("token");
  if (token === null) {
    return Promise.reject(new Error("Can not fetch advert without login"));
  }

  let options = {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      "token": token
    },
    body: body,
  };

  return fetch("/api/preview", options).then((response) => {
    if (!response.ok) {
      console.log("BAAAAAAD #1")
      let err = new Error(`Failed to fetch /preview. Status: ${response.status}`);
      return Promise.reject(err);
    }

    return response.json()
  }).then((json) => {
    return json;
  });
}

export async function sync(data) {
  let token = sessionStorage.getItem("token");
  if (token === null) {
    return Promise.reject(new Error("Can not do sync without login"));
  }

  let options = {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      "token": token
    },
    body: JSON.stringify(data)
  };

  return fetch("/api/sync", options).then((response) => {
    if (!response.ok) {
      let err = new Error(`/sync request failed. status: ${response.status}`);
      err.status = response.status;
      return Promise.reject(err);
    }

    return response.json()
  }).then((json) => {
    // Obsolete key, the whole state is stored now
    localStorage.removeItem("subscriptions");
    var lang = get_user_language_code();
    if (lang !== json.language_code && json.language_code) {
      localStorage.setItem("lang", json.language_code);
      // The locale store was initialized from localStorage on page load,
      // which can be stale when the language was changed through the bot.
      // Setting it here switches the whole UI without a reload.
      locale.set(json.language_code);
    }

    return json;
  }).catch((reason) => {
    console.log(`Sync failed: ${reason.message}`);
    let err = reason;
    if (reason.status === 413)
      err = new Error("Maximum 3 subscriptions");
    else if (reason.status === 500)
      err = new Error("Server is buggy");
    else if (reason.status !== 200)
      err = new Error("Error /sync");
    // Keep the status for the caller (e.g. sync_cycle's 413 handling)
    err.status = reason.status;
    return Promise.reject(err);
  });
}

export async function init() {
  if (['android', 'ios'].includes(Telegram.WebApp.platform)) {
    Telegram.WebApp.requestFullscreen();
    document.body.setAttribute('class', 'mobile');
  }

  Telegram.WebApp.disableVerticalSwipes();
  Telegram.WebApp.expand();
  // Init TWA
  Telegram.WebApp.ready();

  if (Telegram.WebApp.initData === "") {
    let err = new Error(`No Telegram data`);
    return Promise.reject(err);
  }

  return login(Telegram.WebApp.initData).then(() => {
    console.log("Successfully logged in");
  }).catch((reason) => {
    console.log(`Login failed: ${reason.message}`);
  });
}

function areSubscriptionsEqual(obj1, obj2) {
  // Remove fields which do not define the subscription
  const removeIds = (obj) => {
    const { id, created_at, chat_id, metadata, ...rest } = obj;
    return rest;
  };

  // Sort arrays and keys for consistent comparison
  const normalize = (obj) => {
    const normalized = {};

    for (const key of Object.keys(obj).sort()) {
      if (Array.isArray(obj[key])) {
        normalized[key] = [...obj[key]].sort();
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        normalized[key] = normalize(obj[key]);
      } else {
        normalized[key] = obj[key];
      }
    }

    return normalized;
  };

  const clean1 = removeIds(obj1);
  const clean2 = removeIds(obj2);

  return JSON.stringify(normalize(clean1)) === JSON.stringify(normalize(clean2));
}

export function validateSubscription(obj) {
  // Check if obj is an object
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return { valid: false, error: 'Root must be an object' };
  }

  // chat_id and created_at are assigned by the backend on the first
  // successful sync. Pending subscriptions (their sync failed or has not
  // run yet) legitimately do not have them and are recovered through the
  // journal on the next sync_pending().
  const pending = obj.metadata && obj.metadata.status === log.SubscriptionStatus.Pending;
  const requiredKeys = [
    'area',
    'floorplans',
    'id',
    'locations',
    'price',
    'sources',
    'no_duplicates',
    'advert_type',
    'metadata',
  ];
  if (!pending) {
    requiredKeys.push('chat_id', 'created_at');
  }
  const actualKeys = Object.keys(obj);

  // Check if all required keys exist
  const missingKeys = requiredKeys.filter(key => !actualKeys.includes(key));
  if (missingKeys.length > 0) {
    return { valid: false, error: `Missing keys: ${missingKeys.join(', ')}` };
  }

  // Check for extra keys
  const extraKeys = actualKeys.filter(key => !requiredKeys.includes(key));
  if (extraKeys.length > 0) {
    return { valid: false, error: `Extra keys: ${extraKeys.join(', ')}` };
  }

  return { valid: true };
}

// Sync the journal to the backend. On failure the sync is repeated 3 times
// with 3 seconds delay. If it still fails, nothing is done — the journal
// stays and will be synced on the next sync_pending() (next add/remove or
// next application open).
let syncing = false;
let sync_queued = false;

const SYNC_RETRIES = 3;
const SYNC_RETRY_MS = 3000;

/**
 * @param {{ subscriptions: any[], log: any[] }} state
 * @returns {Promise<boolean>}
 */
async function sync_cycle(state) {
  if (state.log.length === 0) {
    return true;
  }

  for (let attempt = 0; attempt <= SYNC_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, SYNC_RETRY_MS));
    }

    try {
      const sent = state.log.slice();
      const json = await sync({
        subscriptions: state.subscriptions,
        log: sent,
      });

      // Backend acknowledged the sent events. Events added meanwhile are
      // kept in the journal and re-applied on the new state.
      log.ack_backend_state(state, json, sent);
      return true;
    } catch (reason) {
      console.log(`Sync attempt ${attempt + 1}/${SYNC_RETRIES + 1} failed: ${reason.message}`);

      const status = /** @type {any} */ (reason).status;
      if (status === 413) {
        // The user is at the maximum number of subscriptions. Pending Add
        // events can not be applied until something is removed — drop them
        // instead of retrying forever. Remove events are kept, they only
        // lower the count and are synced in a fresh cycle.
        log.drop_pending_adds(state);
        return state.log.length === 0 || sync_cycle(state);
      }
    }
  }

  return false;
}

export function sync_pending(state) {
  if (state.log.length === 0) {
    return;
  }
  if (syncing) {
    sync_queued = true;
    return;
  }

  syncing = true;
  (async () => {
    try {
      do {
        sync_queued = false;
        const synced = await sync_cycle(state);
        if (!synced) {
          break;
        }
        // Events which arrived during the sync are still in the journal
      } while (state.log.length > 0 || sync_queued);

      if (state.log.length > 0) {
        console.log("Sync failed, journal is kept for the next sync.");
      }
    } finally {
      syncing = false;
    }
  })();
}

// Marker value of the "custom drawn region" location. Must match the
// backend's expectation in subscription.store().
export const CUSTOM_REGION_VALUE = 696969;

export function subscribe(state, filter) {
  if (state.subscriptions.length == 3) {
    return {
      text: translate('lib.max_subs', { default: 'Max 3 subscriptions' }),
      color: "var(--tg-theme-destructive-text-color)",
      ok: false
    };
  }

  // The region comes from the global state (see Map.svelte's updateRegion()).
  // A JSON round-trip unwraps the $state proxy — a reactive proxy can not be
  // structuredClone()d below and must not leak into the journal.
  const geometry = state.region.modified ? state.region.geojson.geometry : null;
  const hasRegion = Boolean(
    geometry &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0
  );
  var custom = hasRegion ? JSON.parse(JSON.stringify(geometry)) : {};

  const form = document.getElementById("filter-form");
  const formData = new FormData(form);

  let price = {
    min: formData.get('mipr'),
    max: formData.get('mapr'),
  };
  let area = {
    min: formData.get('miar'),
    max: formData.get('maar'),
  };
  var predefined = formData.get("location") === null ? 0 : parseInt(formData.get("location"));
  // predefined and custom must stay in lockstep: the backend reads the drawn
  // polygon only when predefined is the custom marker. A stale "Custom"
  // selection without an actual region falls back to Prague.
  if (!hasRegion && predefined === CUSTOM_REGION_VALUE) {
    predefined = 0;
  }
  var subscription = {
    id: 0,
    advert_type: formData.get("advert_type"),
    floorplans: formData.getAll("floorplan"),
    price: {
      min: ((price.min !== "") ? parseInt(price.min) : null),
      max: ((price.max !== "") ? parseInt(price.max) : null),
    },
    area: {
      min: ((area.min !== "") ? parseInt(area.min) : null),
      max: ((area.max !== "") ? parseInt(area.max) : null),
    },
    locations: {
      predefined: predefined,
      custom: custom
    },
    sources: formData.getAll("source"),
    no_duplicates: (formData.get('no_duplicates') === "on")
  };

  console.log("Subscription to save:");
  console.log(subscription);

  if (state.subscriptions.some((element) => areSubscriptionsEqual(element, subscription))) {
    return {
      text: translate('lib.already_exists', { default: 'This subscription already exists' }),
      color: "var(--tg-theme-destructive-text-color)",
      ok: false
    };
  }

  log.log_item(state, {
    type: log.ItemType.Add,
    id: 0,
    data: structuredClone(subscription),
  });

  sync_pending(state);

  return {
    text: translate('lib.sub_created', { default: 'Subscription created' }),
    color: "#00ad00",
    ok: true
  };
}

export function unsubscribe(state, idx) {
  console.log(`Removing ${idx}`);

  if (idx >= state.subscriptions.length) {
    console.log(`Index ${idx} out of bound ${state.subscriptions.length}`);
    return;
  }

  const sub = state.subscriptions[idx];

  if (sub.metadata && sub.metadata.status === log.SubscriptionStatus.Pending) {
    // The subscription was not created on the backend yet, cancel the
    // pending Add event instead of journaling a Remove
    log.cancel_add(state, sub);
    return;
  }

  log.log_item(state, {
    type: log.ItemType.Remove,
    id: sub.id,
  });

  sync_pending(state);
}

/**
 * @param {{ coordinates: number[][][] }} polygon - The GeoJSON polygon
 * @param {number} targetSize - The size (in abstract units) of the shape's longest side. Defaults to 70.
 * @param {number} padding - The space around the shape within the ViewBox. Defaults to 10.
 * @param {{ coordinates: number[][][] }} [reference] - Optional GeoJSON polygon drawn in the same
 *   coordinate space behind the main shape (e.g. the outline of Prague).
 */
function createSvgFromPolygon(polygon, targetSize = 70, padding = 10, reference) {
  const latLngs = polygon.coordinates[0];
  const referenceLatLngs = reference ? reference.coordinates[0] : [];

  // 1. Calculate center latitude for projection correction
  const lats = latLngs.concat(referenceLatLngs).map(p => p[1]);
  const minRawLat = Math.min(...lats);
  const maxRawLat = Math.max(...lats);
  const centerLat = (minRawLat + maxRawLat) / 2;

  // Cosine correction for X-axis (Longitude)
  const xCorrection = Math.cos(centerLat * (Math.PI / 180));

  // 2. Convert to simple x/y with correction
  /** @param {number[][]} latLngs */
  const project = (latLngs) => latLngs.map(p => ({
    x: p[0] * xCorrection,
    y: -p[1]
  }));

  const points = project(latLngs);
  const referencePoints = project(referenceLatLngs);

  // 3. Get Bounds of the region and the reference combined, so both are
  // projected with one shared scale
  const xs = points.concat(referencePoints).map(p => p.x);
  const ys = points.concat(referencePoints).map(p => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  const rawWidth = maxX - minX;
  const rawHeight = maxY - minY;

  // 4. SCALE TO TARGET SIZE
  // Use the new targetSize argument instead of hardcoded 70
  const scaleFactor = targetSize / Math.max(rawWidth, rawHeight);

  // 5. Transform points
  /** @param {{ x: number, y: number }[]} points */
  const transform = (points) => points.map(p => ({
    x: (p.x - minX) * scaleFactor,
    y: (p.y - minY) * scaleFactor
  }));

  const scaledPoints = transform(points);
  const scaledReferencePoints = transform(referencePoints);

  const scaledWidth = rawWidth * scaleFactor;
  const scaledHeight = rawHeight * scaleFactor;

  // 6. Generate Path
  /** @param {{ x: number, y: number }[]} points */
  const pathFromPoints = (points) => points.map((p, i) => {
    return (i === 0 ? 'M' : 'L') + ` ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  }).join(' ') + ' Z';

  const pathData = pathFromPoints(scaledPoints);
  const cityPathData = pathFromPoints(scaledReferencePoints);

  // 7. ViewBox with Padding
  // Use the new padding argument
  const viewBox = `
                ${-padding} 
                ${-padding} 
                ${scaledWidth + (padding * 2)} 
                ${scaledHeight + (padding * 2)}
            `;

  return {
    viewBox: viewBox,
    pathData: pathData,
    cityPathData: cityPathData,
    width: scaledWidth + (padding * 2),
    height: scaledHeight + (padding * 2),
  };
}

export function generatePictogram(polygon) {
  if (polygon === undefined) {
    return;
  }

  // Outline of the whole of Prague (the root entry of locations.json) behind the
  // drawn region, so its position relative to the city is visible
  return createSvgFromPolygon(polygon, 65, 10, geo[0]);
}

/**
 * Trigger the Telegram haptic feedback. No-op outside Telegram or on
 * clients older than Bot API 6.1.
 * @param {'impactOccurred'|'notificationOccurred'|'selectionChanged'} kind
 * @param {string} [value] - impact style ('light', 'medium', ...) or
 *                           notification type ('error', 'success', 'warning')
 */
export function haptic(kind, value) {
  try {
    Telegram.WebApp.HapticFeedback[kind](value);
  } catch {
    // No Telegram (browser preview) or client older than Bot API 6.1
  }
}

export function setTelegramButton(text, callback, offCallback) {
  Telegram.WebApp.MainButton.offClick(offCallback);
  Telegram.WebApp.MainButton.setText(text);
  Telegram.WebApp.MainButton.onClick(callback);
  /* On-off as on android it doesn't update */
  Telegram.WebApp.MainButton.disable();
  Telegram.WebApp.MainButton.enable();
  Telegram.WebApp.MainButton.show();
}

export function openLinkButton(url) {
  Telegram.WebApp.MainButton.setParams({
    text: translate('lib.telegram_button_open', { default: 'Open' }),
  });

  Telegram.WebApp.MainButton.onClick(() => {
    Telegram.WebApp.openLink(url);
  });
  Telegram.WebApp.MainButton.show();
}

export function doneButton(callback) {
  var text = translate('lib.telegram_button_done', { default: 'Done' });
  setTelegramButton(text, callback);
}
